<template>
  <div>
    <template v-for="(param, index) in node.parameters">
      <div
        :key="`input-${index}`"
        class="cf-node-properties__field"
        :class="{'display-flex': param.name === 'hourseOfOperationSetting' }"
      >
        <label
          class="control-label"
          :class="{'m-b-0 m-t-5': param.name === 'hourseOfOperationSetting' }"
        >
          {{ param.displayName }} <span v-if="!param.isOptional">*</span>
          <i
            v-tooltip.top="param.description"
            class="fa fa-question-circle m-l-5"
            aria-hidden="true"
          />
        </label>
        <InputSearchableSelect
          v-if="param.name === 'timeZoneId'"
          v-model="settingsCopy[param.name]"
          :config="timeZoneOptions"
          :required="!param.isOptional"
          :error="getFieldError(propertyErrors, param.name)"
          @input="inputChange(param.name, $event)"
        />
        <MultipleToggle
          v-if="param.name === 'hourseOfOperationSetting'"
          :value="hoursType"
          :options="hoursTypeOptions"
          class="cf-node-properties__field-btn-group cf-node-properties__multiple-toggle-setting"
          @onToggle="hoursTypeChange($event)"
        />
      </div>

      <template v-if="param.name === 'hourseOfOperationSetting'">
        <div
          v-if="getFieldError(propertyErrors, 'hoursOfOperationSetting')"
          :key="`errors-${index}`"
          class="cf-node-properties__input-error m-b-10"
        >
          Hours of operation must be set below.
        </div>

        <template v-if="hoursType === 'basic'">
          <div
            :key="`basic-${index}`"
            class="cf-node-properties__hours-filter-day"
          >
            <HoursFilterTimeValues
              :hours-type="hoursType"
              :open-time.sync="hoursOfOperationValues[0].openTime.time"
              :close-time.sync="hoursOfOperationValues[0].closeTime.time"
            />
            <div
              id="breaksDay0"
              class="cf-node-properties__hours-filter-breaks-wrapper"
            >
              <HoursFilterBreakForm
                @addBreak="addBreak($event.timeBreak, 0)"
                @toggleBreakForm="toggleBreakForm($event, 0)"
              />
              <HoursFilterBreaks
                v-if="hoursOfOperationValues[0].breaks && hoursOfOperationValues[0].breaks.length"
                :breaks="hoursOfOperationValues[0].breaks"
              />
            </div>
          </div>
        </template>
        <template v-if="hoursType === 'advanced'">
          <div
            v-for="(hours, dayIndex) in hoursOfOperationValues"
            :key="`day-${index}-${dayIndex}`"
            class="cf-node-properties__hours-filter-day"
          >
            <HoursFilterTimeValues
              :hours-type="hoursType"
              :is-open.sync="hours.isOpen"
              :open-time.sync="hours.openTime.time"
              :close-time.sync="hours.closeTime.time"
              :day="days[dayIndex]"
              :disabled="!hours.isOpen"
            />
            <div
              :id="`breaksDay${dayIndex}`"
              class="cf-node-properties__hours-filter-breaks-wrapper"
            >
              <HoursFilterBreakForm
                :index="dayIndex"
                @addBreak="addBreak($event.timeBreak, dayIndex)"
                @toggleBreakForm="toggleBreakForm($event, dayIndex)"
              />
              <HoursFilterBreaks
                v-if="hours.breaks && hours.breaks.length"
                :breaks="hours.breaks"
              />
            </div>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import moment from 'moment';
import { merge, isEqual } from 'lodash';
import {
  getFieldError,
  createSettingsHierarchy,
} from '../../../../helpers/node';
import {
  hoursOfOperation,
  days
} from '../../../../helpers/constants';
import messages from '../../../../locales';
import InputSearchableSelect from '../Form/InputSearchableSelect';
import HoursFilterBreaks from './HoursFilterBreaks';
import HoursFilterBreakForm from './HoursFilterBreakForm';
import HoursFilterTimeValues from './HoursFilterTimeValues';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle';
import nodePropsMixin from '@/modules/ivrs/mixins/nodePropsMixin';
import cloneDeep from 'lodash/cloneDeep';

const timeZoneConfig = {
  reduce: (option) => option.id,
  label: 'display',
};

export default {
  name: 'HoursFilterNodeProps',
  components: {
    InputSearchableSelect,
    MultipleToggle,
    HoursFilterBreaks,
    HoursFilterBreakForm,
    HoursFilterTimeValues,
  },

  mixins: [nodePropsMixin],
  
  data() {
    return {
      settingsCopy: {},
      nodeLocalCopy: {},
      hoursType: 'basic',
      hoursOfOperationValues: hoursOfOperation,
      days: Object.freeze(days)
    };
  },

  computed: {
    ...mapState([
      'timezones'
    ]),

    ...mapGetters('callFlows', [
      'nodesValidationMapping'
    ]),

    timeZoneOptions() {
      return {
        options: this.timezones,
        ...timeZoneConfig,
        placeholder: 'Select Timezone'
      };
    },

    nodeSettings: {
      get() {
        return this.settingsCopy;
      },

      set(value) {
        this.$emit('update:node', {
          ...this.nodeLocalCopy,
          settings: value
        });
        this.settingsCopy = value;
      }
    }
  },
  watch: {
    node: {
      handler(newValue) {
        this.nodeLocalCopy = cloneDeep(newValue);

        this.buildNodeSettings();
      },
      immediate: true,
    },

    hoursOfOperationValues: {
      handler: function() {
        this.translateHoursOfOperation();
      },
      deep: true
    },
  },
  created() {
    if (!this.timezones?.length) {
      this.getTimezones();
    }
        
    this.hoursTypeOptions = [
      {
        name: 'Basic',
        value: 'basic'
      },
      {
        name: 'Advanced',
        value: 'advanced'
      }
    ];

    this.buildNodeSettings();

    this.setHoursOfOperation(this.hoursOfOperationValues);
  },

  methods: {
    ...mapActions(['getTimezones']),

    getFieldError,

    hoursTypeChange(event) {
      this.hoursType = event.value;
      if (event.value === 'advanced') {
        // Deep clone the first element to apply to each day for advanced mode
        const updatedHoursOfOperationValues = this.days.map((day) => {
          const firstElementData = cloneDeep(this.hoursOfOperationValues[0]);
          return {
            ...firstElementData,
            isClosed: false,
            isOpen: true,
            day: day
          };
        });

        this.hoursOfOperationValues = updatedHoursOfOperationValues;
      }
      this.translateHoursOfOperation();
    },
    
    buildNodeSettings() {
      // Sets value of settings data
      this.settingsCopy = createSettingsHierarchy(this.nodeLocalCopy);

      // Merges built settings hierarchy with current node settings
      this.settingsCopy = merge(this.nodeSettings.root, this.nodeLocalCopy.settings);

      // Set hours of operation settings
      this.initializeHoursType(this.nodeSettings?.hourseOfOperationSetting);
      this.initializeHoursOfOperation(this.nodeSettings?.hourseOfOperationSetting);
    },

    initializeHoursType(hoursOfOperation) {
      if (hoursOfOperation) {
        const holdValue = hoursOfOperation.every((day) => {
          if (isEqual(day,hoursOfOperation[0])) {
            return true;
          }
        });
        this.hoursType = holdValue ? 'basic' : 'advanced';
      } else {
        this.hoursType = 'basic';
      }
    },

    initializeHoursOfOperation(hoursOfOperation) {
      if (hoursOfOperation) {
        this.hoursOfOperationValues = hoursOfOperation.map((x, i) => {
          return {
            breaks: x.breaks ? x.breaks : [],
            day: this.days[i],
            openTime: {
              time: `${moment(x.openTime.hour, 'H').format('HH')}:${moment(x.openTime.minute, 'm').format('mm')}`,
              hour: moment(x.openTime.hour, 'H').format('HH'),
              minute: moment(x.openTime.minute, 'm').format('mm')
            },
            closeTime: {
              time: `${moment(x.closeTime.hour, 'H').format('HH')}:${moment(x.closeTime.minute, 'm').format('mm')}`,
              hour: moment(x.closeTime.hour, 'H').format('HH'),
              minute: moment(x.closeTime.minute, 'm').format('mm')
            },
            isClosed: x.isClosed,
            isOpen: !x.isClosed,
            inverted: x.inverted
          };
        });
      }
    },
    addBreak(timeBreak,index) {
      this.hoursOfOperationValues[index].breaks.push(timeBreak);
    },
    toggleBreakForm(value,index) {
      const breaksDay = document.getElementById('breaksDay'+index);
      if (value) {
        breaksDay.classList.add('open');
      } else {
        breaksDay.classList.remove('open');
      }
    },
    translateHoursOfOperation() {
      if (this.hoursType == 'basic') {
        const hoursOfOperation = this.hoursOfOperationValues.map(() => {
          return {
            breaks: this.hoursOfOperationValues[0].breaks,
            openTime: {
              hour: ~~moment(this.hoursOfOperationValues[0].openTime.time, 'HH:mm').format('H'),
              minute: ~~moment(this.hoursOfOperationValues[0].openTime.time, 'HH:mm').format('m')
            },
            closeTime: {
              hour: ~~moment(this.hoursOfOperationValues[0].closeTime.time, 'HH:mm').format('H'),
              minute: ~~moment(this.hoursOfOperationValues[0].closeTime.time, 'HH:mm').format('m')
            },
            inverted: this.hoursOfOperationValues[0].inverted,
            isClosed: false
          };
        });

        this.setHoursOfOperation(hoursOfOperation);
      } else if (this.hoursType == 'advanced') {
        const hoursOfOperation = this.hoursOfOperationValues.map((day) => {
          return {
            breaks: day.breaks,
            openTime: {
              hour: ~~moment(day.openTime.time, 'HH:mm').format('H'),
              minute: ~~moment(day.openTime.time, 'HH:mm').format('m')
            },
            closeTime: {
              hour: ~~moment(day.closeTime.time, 'HH:mm').format('H'),
              minute: ~~moment(day.closeTime.time, 'HH:mm').format('m')
            },
            inverted: day.inverted,
            isClosed: !day.isOpen
          };
        });

        this.setHoursOfOperation(hoursOfOperation);
      }
    },

    setHoursOfOperation(hoursOfOperationValues) {
      this.nodeSettings = {
        ...this.nodeSettings,
        hourseOfOperationSetting: hoursOfOperationValues
      };
    },

    inputChange() {
      this.$emit('update:node', {
        ...this.nodeLocalCopy,
        settings: this.settingsCopy
      });
    },
  },
  i18n: {
    messages,
    locale: 'en',
  },
};
</script>